import React, { useCallback, useMemo, useRef } from 'react';

import { Icon, Skeleton } from '@rbilabs/universal-components';
import { randomUUID as uuidv4 } from 'expo-crypto';
import { Image } from 'expo-image';
import { useAppTakeoverCartItem } from 'features/app-takeover/hooks/use-app-takeover';
import { useAppTakeoverCart } from 'features/app-takeover/hooks/use-app-takeover-cart';
import { isHydratedReward } from 'features/cart/utils/minimal-cart';
import { logButtonClick } from 'features/navigation/utils/analytics';
import { ImageBackground, StyleSheet } from 'react-native';

import { ILegacyCartEntry } from '@rbi-ctg/menu';
import { CartDebugMenu } from 'components/debug-menu/cart-debug';
import FavoriteEntryButton from 'components/favorite-entry-button';
import Picture from 'components/picture';
import { Box } from 'components/ui';
import { useCart } from 'hooks/use-cart';
import { useCartItemEditingTools } from 'hooks/use-cart-item-editing-tools';
import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import useFoodware from 'hooks/use-foodware';
import { useIsEditCartEnabled } from 'hooks/use-is-edit-cart-enabled';
import { useIsLargeFontScale } from 'hooks/use-is-large-font-scale';
import { useIsExtraSmallBreakpoint } from 'hooks/use-media-query';
import { ClickEventComponentNames, CustomEventNames } from 'state/amplitude';
import { useCartContext } from 'state/cart';
import { DISCOUNT_OFFER_CART_ID } from 'state/cart/constants';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { RewardRedemptionMethod } from 'state/loyalty/hooks/types';
import { useGetAvailableRewards } from 'state/loyalty/hooks/use-get-available-rewards';
import { useIsLoyaltyEnabled } from 'state/loyalty/hooks/use-is-loyalty-enabled';
import { useRewardRedemptionLimit } from 'state/loyalty/hooks/use-reward-redemption-limit';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { primitive, spacing } from 'styles/constants';
import { getMenuObjectCartQuantity } from 'utils/cart';
import { isCartEntryItem } from 'utils/cart/is-cart-type';
import { HapticsNotificationType, hapticNotification } from 'utils/haptic';

import { CartItemOffer } from './cart-item-offer';
import { CartItemPriceAndCalories } from './cart-item-price-and-calories';
import { ConfiguredTakeoverHeader } from './configured-takeover-header';
import {
  ComposedCartItemType,
  useComposeCartItemUIData,
} from './hooks/use-compose-cart-item-ui-data';
import { useRedeemReward } from './hooks/use-redeem-reward-state';
import { Price } from './price';
import { RedeemReward } from './redeem-reward';
import {
  CartItemButton,
  CartItemButtonText,
  CartItemDescription,
  CartItemEditButtonContainer,
  CartItemEditButtonContainerLeft,
  CartItemEditButtonSpacer,
  CartItemEditContainer,
  CartItemHeaderText,
  CartItemInfoContainer,
  CartItemName,
  Item,
} from './styled';
import { ICartItemProps } from './types';

const DISABLED_COLOR = primitive.$blackOpacity50;
const ENABLED_COLOR = primitive.$bbqBrown;

export const CartItem: React.FC<React.PropsWithChildren<ICartItemProps>> = React.memo(
  ({
    item,
    readOnly = false,
    rewardApplied = false,
    selectedOfferId,
    onAnnounceA11yMessage,
    appliedComboReward,
  }) => {
    const hasSelectedOffer = !!selectedOfferId;

    const isExtraSmallScreen = useIsExtraSmallBreakpoint();
    const isLargeFontScale = useIsLargeFontScale();
    const isEditOfferButtonEnabled = useFlag(LaunchDarklyFlag.ENABLE_EDIT_OFFERS_CART_BUTTON);
    const isDebugMenuEnabled = useFlag(LaunchDarklyFlag.ENABLE_DEBUG_TOOL);
    const enableRewardUnstacking = useFlag(LaunchDarklyFlag.ENABLE_REWARD_UNSTACKING);
    const rewardRedemptionLimit = useRewardRedemptionLimit();
    const isUnifiedMenuEnabled = useEnableUnifiedMenu();

    const cartEntryIsComboReward = item.cartId === appliedComboReward?.cartId;
    const dispatch = useAppDispatch();
    const offerSkeletonRenderedRef = useRef(false);

    // TODO: check if we need to evaluate LoyaltyOffer as cartHasIncentive
    const isEditCartEnabled = useIsEditCartEnabled({
      cartHasIncentive: hasSelectedOffer || rewardApplied,
    });

    const { handleEditItem, handleRemoveItem } = useCartItemEditingTools({
      item,
      isOffer: hasSelectedOffer,
    });

    const composedItem = useComposeCartItemUIData(item);
    // `item.name` is baked in as a plain string at add-to-cart time and never re-resolved after
    // a later app language change; `composedItem?.name` is the fresh, currently-localized value.
    // Used for every guest-facing surface for this item — visible text, accessibility labels,
    // and announcements — so screen-reader users hear the same name sighted guests see.
    const displayName = composedItem?.name ?? item.name;

    const itemKey = useMemo(() => item.cartId || item._id, [item._id, item.cartId]);
    const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
    const appliedLoyaltyRewardsArray = useAppSelector(
      selectors.loyalty.selectAppliedLoyaltyRewardsArray
    );
    const loyaltyEnabled = useIsLoyaltyEnabled();
    const { getAvailableRewardFromCartEntry } = useGetAvailableRewards();
    const { addItemToCart, cartEntries } = useCart();
    const isLoadingOffers = useAppSelector(selectors.loyalty.selectOffersLoading);
    const { isOrderPriceLoading } = useCartContext();
    const { indexedMenu } = useUnifiedMenuContext();

    const unifiedMenuEntry = indexedMenu.find(item._id);

    const { maxCartQuantityMet } = getMenuObjectCartQuantity({
      menuObject: item,
      cartEntries,
      unifiedMenuEntry,
    });

    const loyaltyRewardAvailable = getAvailableRewardFromCartEntry(item);
    const { isFoodware } = useFoodware();
    const { hasSufficientBalance, limitPerOrderMet } = useRedeemReward(item);

    const takeoverFontFamily = useAppTakeoverCart()?.fontFamily;
    const cartItemHeaderTextStyle = useMemo(
      () => (takeoverFontFamily ? { fontFamily: takeoverFontFamily } : undefined),
      [takeoverFontFamily]
    );
    const itemTakeoverConfig = useAppTakeoverCartItem(item.url);

    const loyaltyOffer = appliedOffers.find(lylOffer => lylOffer.cartId === item.cartId);
    const isSingleItem = isCartEntryItem(item) || (isUnifiedMenuEnabled && isHydratedReward(item));
    const itemHasHeader = Boolean(
      !isSingleItem ||
        loyaltyOffer?.id ||
        itemTakeoverConfig?.cartItemHeader ||
        itemTakeoverConfig?.cartItemImage
    );
    const isLoading = isOrderPriceLoading || isLoadingOffers;
    const showSkeleton = !offerSkeletonRenderedRef.current && isLoading;
    const isExtra = item.isExtra;
    const isItemEditableInline = !hasSelectedOffer && !isExtra && !loyaltyOffer;
    const hasOffer = (hasSelectedOffer && selectedOfferId === item.sanityId) || loyaltyOffer;
    const showFavorite = !hasOffer && !isExtra;
    const showRedeemRewardButton =
      loyaltyEnabled && loyaltyRewardAvailable && isItemEditableInline && !appliedComboReward;

    const handleEditItemButtonPress = useCallback(() => {
      if (isLoading) {
        return;
      }

      handleEditItem();
    }, [handleEditItem, isLoading]);

    const handleDuplicateItem = useCallback(() => {
      if (isLoading) {
        return;
      }

      logButtonClick({
        attributes: {
          component: ClickEventComponentNames.BUTTON,
          name: CustomEventNames.BUTTON_CLICK_DUPLICATE_ITEM,
        },
      });

      hapticNotification({ type: HapticsNotificationType.SUCCESS });
      const newCartId = uuidv4();

      const canRedeemReward = hasSufficientBalance && !limitPerOrderMet;
      const currentCartEntryHasRewardApplied = appliedLoyaltyRewardsArray?.some(
        reward => reward.cartId === item.cartId
      );

      addItemToCart(
        { ...item, cartId: newCartId, lineId: newCartId } as ILegacyCartEntry,
        undefined,
        undefined,
        true
      );

      onAnnounceA11yMessage(t('addToCartSuccess', { itemName: displayName }));

      if (
        currentCartEntryHasRewardApplied &&
        canRedeemReward &&
        loyaltyRewardAvailable?.rewardBenefitId
      ) {
        dispatch(
          actions.loyalty.applyReward({
            rewardBenefitId: loyaltyRewardAvailable.rewardBenefitId,
            cartId: newCartId,
            quantity: 1,
            redemptionMethod: RewardRedemptionMethod.MOBILE,
            enableRewardUnstacking,
            rewardRedemptionLimit,
          })
        );
      }
    }, [
      item,
      displayName,
      onAnnounceA11yMessage,
      hasSufficientBalance,
      limitPerOrderMet,
      appliedLoyaltyRewardsArray,
      loyaltyRewardAvailable?.rewardBenefitId,
      addItemToCart,
      dispatch,
      isLoading,
      enableRewardUnstacking,
      rewardRedemptionLimit,
    ]);

    const onRemoveItemPress = useCallback(() => {
      if (isLoading) {
        return;
      }

      hapticNotification({ type: HapticsNotificationType.WARNING });
      handleRemoveItem();
      logButtonClick({
        attributes: {
          component: ClickEventComponentNames.BUTTON,
          name: CustomEventNames.BUTTON_CLICK_CART_ITEM_REMOVED,
        },
      });
    }, [handleRemoveItem, isLoading]);

    const ItemDescription = useMemo(() => {
      return ({ composedCartItem }: { composedCartItem: ComposedCartItemType }) => {
        const { name, image } = composedCartItem;

        return (
          <CartItemDescription>
            <Box style={styles.pictureContainer}>
              <Picture size="$14" alt={name} image={image} />
            </Box>
            <Box style={styles.itemDescriptionTextContainer}>
              <CartItemName
                style={!itemHasHeader && styles.cartItemNameWithoutHeader}
                testID="cart-item-label"
              >
                {name}
              </CartItemName>
              <CartItemPriceAndCalories cartItem={composedCartItem} isSingleItem={isSingleItem} />
            </Box>
          </CartItemDescription>
        );
      };
    }, [isSingleItem, itemHasHeader]);

    const duplicateButton = useMemo(() => {
      if (cartEntryIsComboReward) {
        return null;
      }

      return (
        <CartItemButton
          onPress={handleDuplicateItem}
          testID="duplicate-button"
          keepIconSize
          accessibilityLabel={t('duplicate', { itemName: displayName })}
          leftIcon={<Icon name="custom:DuplicateItem" size="md" />}
          disabled={maxCartQuantityMet || isLoading}
          color={maxCartQuantityMet || isLoading ? DISABLED_COLOR : ENABLED_COLOR}
        />
      );
    }, [cartEntryIsComboReward, handleDuplicateItem, displayName, maxCartQuantityMet, isLoading]);

    // Render the header for offers or non single items.
    const header = useMemo(() => {
      if (loyaltyOffer?.id) {
        return (
          <CartItemOffer
            isLoading={showSkeleton}
            loyaltyOfferId={loyaltyOffer.id}
            cmsOfferId={loyaltyOffer.cmsId}
            skeletonRenderedRef={offerSkeletonRenderedRef}
          />
        );
      }

      if (itemTakeoverConfig?.cartItemHeader) {
        return <ConfiguredTakeoverHeader itemUrl={item.url} title={displayName} />;
      }

      const headerContent = (
        <>
          {itemTakeoverConfig?.cartItemImage && (
            <Image source={itemTakeoverConfig.cartItemImage} style={styles.headerImage} />
          )}
          <CartItemHeaderText style={cartItemHeaderTextStyle} testID="cart-item-label">
            {displayName}
          </CartItemHeaderText>
        </>
      );

      return (
        (!isSingleItem || itemTakeoverConfig?.cartItemImage) && (
          <>
            {itemTakeoverConfig?.cartItemBackgroundImage ? (
              <ImageBackground
                style={styles.header}
                source={itemTakeoverConfig.cartItemBackgroundImage}
                resizeMode="stretch"
                imageStyle={{
                  width: '100%',
                  height: '100%',
                  borderTopLeftRadius: primitive.$borderRadius,
                  borderTopRightRadius: primitive.$borderRadius,
                }}
              >
                {headerContent}
              </ImageBackground>
            ) : (
              <Box style={styles.header}>{headerContent}</Box>
            )}
          </>
        )
      );
    }, [
      loyaltyOffer?.id,
      loyaltyOffer?.cmsId,
      isSingleItem,
      displayName,
      showSkeleton,
      itemTakeoverConfig?.cartItemImage,
      itemTakeoverConfig?.cartItemBackgroundImage,
      itemTakeoverConfig?.cartItemHeader,
      cartItemHeaderTextStyle,
      item.url,
    ]);

    if (item.isDonation || isFoodware(item)) {
      return null;
    }

    /**
     * The whole order discounts should not show in the list of cart items.
     * The details for this is explicitly shown outside of the cart item list.
     * The offer must be applied to the cart items, so we're simply removing it
     * visually from the list here.
     */
    if (loyaltyOffer?.cartId === DISCOUNT_OFFER_CART_ID) {
      return null;
    }

    const favoriteAbsoluteButton = showFavorite && (
      <Box
        style={[styles.favoriteAbsoluteButton, itemTakeoverConfig?.cartItemHeader?.favoriteAction]}
      >
        <FavoriteEntryButton cartEntry={item} disabled={isLoading} />
      </Box>
    );

    if (!composedItem) {
      return null;
    }

    return (
      <Item testID={`cart-item-${itemKey}`}>
        {itemHasHeader ? (
          <Box style={styles.headerContainer} testID="cart-item-header-container">
            {header}
            {favoriteAbsoluteButton}
          </Box>
        ) : (
          favoriteAbsoluteButton
        )}
        <CartItemInfoContainer>
          {isDebugMenuEnabled && <CartDebugMenu cartEntry={item} />}
          {composedItem.children?.length ? (
            composedItem.children.map((composedCartItem, key) => {
              return <ItemDescription key={key} composedCartItem={composedCartItem} />;
            })
          ) : (
            <ItemDescription composedCartItem={composedItem} />
          )}
        </CartItemInfoContainer>

        {!readOnly && (
          <>
            <CartItemEditContainer
              testID="cart-item-edit-container"
              style={isLargeFontScale ? styles.stackedEditContainer : undefined}
            >
              <CartItemEditButtonContainerLeft
                style={[
                  styles.editButtonContainerLeft,
                  isLargeFontScale && styles.stackedEditButtonContainerLeft,
                ]}
              >
                {hasOffer && showSkeleton ? (
                  <Skeleton height="$7" rounded="8" width={'$17_5'} />
                ) : (
                  <>
                    <CartItemButton
                      keepIconSize
                      accessibilityLabel={`${t('remove')} ${displayName} `}
                      testID="remove-button"
                      onPress={onRemoveItemPress}
                      leftIcon={<Icon variant="bkRemove" size="md" />}
                      disabled={isLoading}
                      color={isLoading ? DISABLED_COLOR : ENABLED_COLOR}
                    />
                    {!loyaltyOffer?.id && !isExtra && (
                      <>
                        <CartItemEditButtonSpacer />
                        {duplicateButton}
                      </>
                    )}
                    {isEditCartEnabled && (loyaltyOffer ? isEditOfferButtonEnabled : true) && (
                      <>
                        <CartItemEditButtonSpacer />
                        <CartItemButton
                          accessibilityLabel={`${t('edit')} ${displayName} `}
                          testID="edit-button"
                          onPress={handleEditItemButtonPress}
                          disabled={isLoading}
                        >
                          <CartItemButtonText color={isLoading ? DISABLED_COLOR : ENABLED_COLOR}>
                            {t('edit')}
                          </CartItemButtonText>
                        </CartItemButton>
                      </>
                    )}
                  </>
                )}
              </CartItemEditButtonContainerLeft>
              {hasOffer && showSkeleton ? (
                <Skeleton height="$7" rounded="12" width="$20" />
              ) : (
                <CartItemEditButtonContainer
                  style={[
                    styles.editButtonContainer,
                    isLargeFontScale && styles.stackedEditButtonContainer,
                  ]}
                >
                  {showRedeemRewardButton && (
                    <RedeemReward
                      isRewardApplied={rewardApplied}
                      item={item}
                      reward={loyaltyRewardAvailable}
                      disabled={isLoading}
                    />
                  )}
                  {(!showRedeemRewardButton || !isExtraSmallScreen || isLargeFontScale) && (
                    <Box style={styles.priceContainer}>
                      <Price item={item} isOffer={hasSelectedOffer} rewardApplied={rewardApplied} />
                    </Box>
                  )}
                </CartItemEditButtonContainer>
              )}
            </CartItemEditContainer>
            {showRedeemRewardButton && isExtraSmallScreen && !isLargeFontScale && (
              <Box style={styles.extraSmallPriceContainer}>
                <Price item={item} isOffer={hasSelectedOffer} rewardApplied={rewardApplied} />
              </Box>
            )}
          </>
        )}
      </Item>
    );
  }
);

const styles = StyleSheet.create({
  editButtonContainer: {
    justifyContent: 'flex-end',
  },
  editButtonContainerLeft: {
    marginRight: spacing.$4,
  },
  extraSmallPriceContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    paddingHorizontal: spacing.$4,
    paddingVertical: spacing.$2,
  },
  stackedEditContainer: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: spacing.$2,
  },
  stackedEditButtonContainerLeft: {
    flex: 0,
    flexWrap: 'wrap',
    marginRight: 0,
    width: '100%',
  },
  stackedEditButtonContainer: {
    width: '100%',
    justifyContent: 'space-between',
  },
  favoriteAbsoluteButton: {
    position: 'absolute',
    right: 0,
    zIndex: 1,
  },
  header: {
    borderBottomWidth: 1,
    borderBottomColor: primitive.$blackOpacity10,
    borderTopLeftRadius: primitive.$borderRadius,
    borderTopRightRadius: primitive.$borderRadius,
    flexDirection: 'row',
    overflow: 'hidden',
    alignItems: 'center',
  },
  headerContainer: {
    alignSelf: 'stretch',
    position: 'relative',
    width: '100%',
  },
  headerImage: {
    aspectRatio: 1,
    height: 50,
    marginLeft: spacing.$3,
  },
  itemDescriptionTextContainer: {
    flex: 1,
    justifyContent: 'center',
  },
  cartItemNameWithoutHeader: {
    marginRight: spacing.$4,
  },
  pictureContainer: {
    paddingRight: spacing.$4,
  },
  priceContainer: {
    flexDirection: 'row',
    marginLeft: spacing.$2,
  },
});
